import gql from 'graphql-tag';

const pathDebuggingQueryPart = `
      paths {
        id

        key
        steps {
          id
          question {
            key
            text
          }
          answer
          parameters
          medicationRecommendations {
            id

            medicationClassRecommendations{
              id
              name
              minDosageUnit
              minDosageValue
              maxDosageUnit
              maxDosageValue

              medicationClass{
                key
                frequency
                administration
                name
              }
            }

            medicationText
            guidelineEntries {
              id

              guideLine {
                key
                text
                source
                classLabel
                levelOfEvidence
                medicationClass
              }
            }
          }
        }
      }
`;

export const createDSSMedicationSchedulesQuery = function (
    fromLastAlgorithm = false,
    includePathDebugging = false
) {
    let mutationParameters = '$patientId: String!, $skipAlgorithm: String!, $forceRefresh: String!, $sinceDate: DateTime';
    let functions = `
          { function: patientId, parameters: [$patientId] }
          { function: skipAlgorithm, parameters: [$skipAlgorithm] }
          { function: forceRefresh, parameters: [$forceRefresh] }
    `;

    if (fromLastAlgorithm){
        functions += `{ function: useAlgorithm, parameters: [$lastRunAlgorithmId] }`;
        mutationParameters += ', $lastRunAlgorithmId: String!'
    }

    return gql`
  mutation DSSMedicationSchedules(${mutationParameters}) {
    DSSMedicationSchedules(
      functions: [${functions}]
      conditions: {
        gate: and
        inputs: [
          { field: deleted, value: "0" }
        ]
      }
    ) {
      medicationSchedules {
        id
        notes
        wasChangedSinceDate(date: $sinceDate)
        lastAuditTrail(mainRole: "patient", afterLastDashboardTargetScreens: ["DSSCardioMedication", "DSSOtherMedication"]) {
          timestamp
        }
        schedule {
          id
          intervals {
            interval
            intervalOn
          }
          monthsOfYear
          daysOfMonth
          daysOfWeek
          weeksOfMonth
          weeksOfYear
          times
          note
        }
        totalAmount: totalAmountOfPillsInjections
        morningAmount: totalAmountOfPillsInjections(
          quartile: "MORNING"
        )
        noonAmount: totalAmountOfPillsInjections(quartile: "NOON")
        afternoonAmount: totalAmountOfPillsInjections(
          quartile: "AFTERNOON"
        )
        eveningAmount: totalAmountOfPillsInjections(
          quartile: "EVENING"
        )
        intakeMoments {
          id
          time
          amount
          reminder
        }
        medication {
          medicationType
          resourceType
          id
          name
          form {
            text
            coding {
              system
              version
              code
              display
            }
          }
          extension {
            url
            value
          }
          classes {
            key
            parentKey
            name
            description
            administration
            parameters {
              key
              name
            }
          }
        }
        dosage {
          id
          sequence
          doseAndRate {
            id
            sequence
            doseQuantity {
              code
              unit
              value
            }
          }
        }
        medicationRecommendations{
            id

            guidelines {
              id
              key
              text
              source
              classLabel
              levelOfEvidence
              medicationClass
            }

            medicationClassRecommendations{
              name
              minDosageUnit
              minDosageValue
              maxDosageUnit
              maxDosageValue

              medicationClass{
                key
                frequency
                administration
              }
            }
        }
        recommended
      }

      algorithm {
        id
        version
        
      warnings{
        id
        text
      }

        ${includePathDebugging ? pathDebuggingQueryPart : ''}

        unhandledMedicationRecommendations {
          id

          guidelines {
            id
            key
            text
            source
            classLabel
            levelOfEvidence
            medicationClass
          }

          medicationClassRecommendations{
              name
              minDosageUnit
              minDosageValue
              maxDosageUnit
              maxDosageValue

              medicationClass{
                key
                frequency
                administration
              }
          }
        }
      }
    }
  }
`;
}

export const createUpdateMyMedicationSchedule = gql`
  mutation createUpdateMyMedicationSchedule(
    $patientId: String
    $id: String
    $name: String!
    $administration: String!
    $dosages: [CreateUpdateMyMedicationScheduleDosage!]
    $notes: String
    $morningAmount: Float
    $noonAmount: Float
    $afternoonAmount: Float
    $eveningAmount: Float
    $schedule: CreateUpdateSchedule
    $classes: [String!]
  ) {
    createUpdateMyMedicationSchedule(
      medicationSchedule: {
        id: $id
        patientId: $patientId
        name: $name
        type: "cardio"
        administration: $administration
        dosages: $dosages
        notes: $notes
        morningAmount: $morningAmount
        noonAmount: $noonAmount
        afternoonAmount: $afternoonAmount
        eveningAmount: $eveningAmount
        classes: $classes
        schedule: $schedule
      }
    ) {
      id
      notes
      schedule {
        id
        intervals {
          interval
          intervalOn
        }
        monthsOfYear
        daysOfMonth
        daysOfWeek
        weeksOfMonth
        weeksOfYear
        times
        note
      }
      totalAmount: totalAmountOfPillsInjections
      morningAmount: totalAmountOfPillsInjections(
        quartile: "MORNING"
      )
      noonAmount: totalAmountOfPillsInjections(quartile: "NOON")
      afternoonAmount: totalAmountOfPillsInjections(
        quartile: "AFTERNOON"
      )
      eveningAmount: totalAmountOfPillsInjections(
        quartile: "EVENING"
      )
      intakeMoments {
        id
        time
        amount
        reminder
      }
      medication {
        resourceType
        id
        name
        form {
          text
          coding {
            system
            version
            code
            display
          }
        }
        extension {
          url
          value
        }
        classes {
          key
          parentKey
          name
          description
          administration
          parameters {
            key
            name
          }
        }
      }
      dosage {
        id
        sequence
        doseAndRate {
          id
          sequence
          doseQuantity {
            code
            unit
            value
          }
        }
      }
      recommended
      guidelines {
        id
        key
        text
        source
        classLabel
        levelOfEvidence
        medicationClass
      }
    }
  }
`;

export const deleteMyMedicationSchedule = gql`
  mutation deleteMyMedicationSchedule($id: String!) {
    deleteMyMedicationSchedule(id: $id)
  }
`;

export const getMedicationClasses = gql`
  {
    medicationClasses {
      key
      name
      description
    }
  }
`;

export const getPatientQuestionnaires = gql`
  query getPatientQuestionnaires($patientId: String!) {
    myQuestionnaireForms(
      functions: { function: patientId, parameters: [$patientId] },
      conditions: { gate: and, inputs: [
        { field: key, operator: in, parameters: ["DSS-INFO"] }
      ] },
      orders: { field: formEntryDate, direction: desc }
    ) {
        questionnaire {
          key
        }
        entries {
          question {
            key
          }
          givenAnswers {
            key
            openAnswer
            openAnswerObject
          }
        }
      }
    }
  `;

export const getAuditTrails = gql`query getAuditTrails($patientId: String!)
  {
    medicationScheduleAuditTrails(functions: [
      { function: patientId, parameters: [$patientId] }
    ]) {
      edges{
        node{
          when
          who
          what
          medicationScheduleId
        }
      }
    }
  }
`;

export const getDssInfo = gql`
  query getDssInfo($patientId: Int!) {
    myDSSInfo(patientId: $patientId){
      subjectId
      hasDiabetesType1
      hasDiabetesType2
      hasDiabetes
      hadMyocardialInfarctionLast12Months
      hadAnAnteriorInfarction
      infarctionType
      hasHistoryOfAtrialFibrillation
      hasNonValvularAtrialFibrillation
      hasAtrialFibrillationWithIndicationForAnticoagulation
      hadPCI
      hadPCILast6Months
      hadPCILastWeek
      lastPCIDate
      hasHighRiskOfGastroInternalBleeding
      hasIntoleranceForAspirin
      CHA2DS2VAScScore
      gender
      hadSecondVascularEventOnStatins
      alreadyOnHighDoseStatinTherapy
      alreadyOnHighDoseStatinEzeTimbeTherapy
      currentLDLMgDL
      suffersFromAnginaEffort
      suffersFromAnginaRest
      hasSymptomaticAngina
      alreadyOnBetaBlocker
      alreadyOnCalciumChannelBlocker
      suffersFromSystolicHeartFailureOrLVDysfunction
      signsOfPulmonarySystemicCongestion
      LVSymptomaticDespiteTreatment
      hasIntoleranceForACEInhibitor
      EGFR
      hasRenalFailureOrHyperKalaemia
      age
      meanSystolicBloodPressure
      meanDiastolicBloodPressure
      currentAmountReceivedHypertensionDrugs
      hasHistoryOfMI
    }
  }
`;